import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { pprApi, usersApi, type PprJob, type PprStatus, type PprReviewType } from '@/lib/apiClient';
import { PPR_STATUSES, PPR_TERMINAL_STATUSES, fmtUsd, fmtDate, daysSince } from './pprShared';
import StartPprModal from './StartPprModal';
import { Button, Card, Badge, StateView, type BadgeTone } from '@/components';

const STATUS_TONE: Record<PprStatus, BadgeTone> = {
  'Initiating': 'info',
  'Survey Collection': 'warning',
  'Awaiting Financials': 'danger',
  'Financials Completed by Accounting': 'info',
  'Report Prep': 'success',
  'Complete': 'neutral',
  'Cancelled': 'neutral',
};

const REVIEW_TYPE_TONE: Record<PprReviewType, BadgeTone> = {
  'Full Review': 'success',
  'Financial Only': 'warning',
};

const css = `
  .ppr-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--ew-s-3);
    margin-bottom: var(--ew-s-5);
  }
  .ppr-toolbar h1 {
    font: var(--ew-fw-semibold) var(--ew-fs-h4)/1 var(--ew-font);
    color: var(--ew-dark);
    margin-right: auto;
  }
  .ppr-toolbar select, .ppr-toolbar input[type="search"] {
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1 var(--ew-font);
    padding: 7px 10px;
    border: 1px solid var(--ew-n-300);
    border-radius: var(--ew-r-md);
    background: #fff;
    color: var(--ew-dark);
  }
  .ppr-toolbar label {
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1 var(--ew-font);
    color: var(--ew-n-600);
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  /* KPI grid wraps shared Card primitives */
  .ppr-kpis {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: var(--ew-s-3);
    margin-bottom: var(--ew-s-5);
  }
  .ppr-kpi-label {
    font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font);
    text-transform: uppercase;
    letter-spacing: var(--ew-ls-caption);
    color: var(--ew-n-500);
    margin-bottom: 6px;
  }
  .ppr-kpi-value {
    font: var(--ew-fw-semibold) var(--ew-fs-h2)/1 var(--ew-font);
    color: var(--ew-dark);
  }
  .ppr-kpi-value--warning { color: var(--ew-danger); }

  /* Status columns */
  .ppr-board {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: var(--ew-s-4);
  }
  .ppr-col {
    background: var(--ew-n-50);
    border: 1px solid var(--ew-n-200);
    border-radius: var(--ew-r-lg);
    padding: var(--ew-s-3);
    min-height: 200px;
  }
  .ppr-col-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: var(--ew-s-3);
  }
  .ppr-col-title {
    font: var(--ew-fw-semibold) var(--ew-fs-body-ui)/1 var(--ew-font);
    color: var(--ew-dark);
  }
  .ppr-col-count {
    font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font);
    color: var(--ew-n-500);
  }

  /* Job cards */
  .ppr-card {
    display: block;
    background: #fff;
    border: 1px solid var(--ew-n-200);
    border-radius: var(--ew-r-md);
    padding: var(--ew-s-3);
    margin-bottom: var(--ew-s-2);
    transition: border-color var(--ew-dur-fast) var(--ew-ease),
                box-shadow var(--ew-dur-fast) var(--ew-ease);
    text-decoration: none;
    color: inherit;
  }
  .ppr-card:hover {
    border-color: var(--ew-dark);
    box-shadow: var(--ew-shadow-1);
  }
  .ppr-card-aging { border-left: 3px solid var(--ew-performance); }
  .ppr-card-escalated { border-left: 3px solid var(--ew-danger); }
  .ppr-card-top {
    display: flex;
    justify-content: space-between;
    gap: var(--ew-s-2);
    margin-bottom: 6px;
  }
  .ppr-card-job {
    font: var(--ew-fw-semibold) var(--ew-fs-body-sm)/1.25 var(--ew-font);
    color: var(--ew-dark);
  }
  .ppr-card-job small {
    display: block;
    font: var(--ew-fw-regular) var(--ew-fs-caption)/1.4 var(--ew-font);
    color: var(--ew-n-500);
    margin-top: 2px;
  }
  .ppr-card-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }
  .ppr-card-stats {
    display: flex;
    justify-content: space-between;
    margin-top: 8px;
    font: var(--ew-fw-regular) var(--ew-fs-caption)/1.4 var(--ew-font);
    color: var(--ew-n-600);
  }
  .ppr-empty {
    text-align: center;
    color: var(--ew-n-400);
    font: var(--ew-fw-regular) var(--ew-fs-caption)/1.4 var(--ew-font);
    padding: var(--ew-s-4) 0;
  }
`;

export default function PprDashboard() {
  const [search, setSearch] = useState('');
  const [reviewTypeFilter, setReviewTypeFilter] = useState<PprReviewType | ''>('');
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Shared `me` query (deduped by React Query — already fetched in App.tsx).
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => usersApi.me(),
    staleTime: 5 * 60 * 1000,
  });
  // Gate on the PERMISSION the API actually enforces, not on role names. The
  // old check hardcoded Admin/PprCoordinator, so a custom role carrying
  // ppr.manage got a read-only-looking UI despite having write access — and if
  // PprCoordinator ever lost ppr.manage, its buttons would show and then 403.
  const canEdit = !!me && (me.roles?.includes('Admin') || !!me.permissions?.includes('ppr.manage'));

  const jobsQuery = useQuery({
    queryKey: ['ppr', 'jobs', { search, reviewTypeFilter, includeCompleted, includeCancelled }],
    queryFn: () =>
      pprApi.listJobs({
        search: search || undefined,
        reviewType: reviewTypeFilter || undefined,
        includeCompleted,
        includeCancelled,
      }),
  });

  const jobs = jobsQuery.data ?? [];

  // Group by status
  const grouped = useMemo(() => {
    const m = new Map<PprStatus, PprJob[]>();
    for (const status of PPR_STATUSES) m.set(status, []);
    for (const j of jobs) {
      const arr = m.get(j.status);
      if (arr) arr.push(j);
    }
    return m;
  }, [jobs]);

  // KPIs. "Active" means neither terminal status — a cancelled review is no more
  // active than a completed one, and counting it would inflate this the moment
  // the Show cancelled box was ticked.
  const kpiActive = jobs.filter((j) => !PPR_TERMINAL_STATUSES.includes(j.status)).length;
  const kpiAwaitingFin = jobs.filter((j) => j.status === 'Awaiting Financials').length;
  const kpiOverdue = jobs.reduce((acc, j) => acc + (j.contactsOverdue ?? 0), 0);

  // Visible columns — each terminal status appears only when asked for. These
  // MUST track the query flags above: a status the API returns but that has no
  // column here would drop those jobs off the board silently.
  const visibleStatuses = PPR_STATUSES.filter((s) => {
    if (s === 'Complete') return includeCompleted;
    if (s === 'Cancelled') return includeCancelled;
    return true;
  });

  return (
    <>
      <style>{css}</style>

      <div className="ppr-toolbar">
        <h1>Post Project Reviews</h1>

        <input
          type="search"
          placeholder="Search job # or name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          value={reviewTypeFilter}
          onChange={(e) => setReviewTypeFilter(e.target.value as PprReviewType | '')}
        >
          <option value="">All types</option>
          <option value="Financial Only">Financial Only</option>
          <option value="Full Review">Full Review</option>
        </select>
        <label>
          <input
            type="checkbox"
            checked={includeCompleted}
            onChange={(e) => setIncludeCompleted(e.target.checked)}
          />
          Show completed
        </label>
        <label>
          <input
            type="checkbox"
            checked={includeCancelled}
            onChange={(e) => setIncludeCancelled(e.target.checked)}
          />
          Show cancelled
        </label>

        {canEdit && (
          <Button variant="primary" onClick={() => setModalOpen(true)}>
            + Start PPR
          </Button>
        )}
      </div>

      <div className="ppr-kpis">
        <Kpi label="Active PPRs" value={kpiActive} />
        <Kpi label="Awaiting Financials" value={kpiAwaitingFin} />
        <Kpi label="Overdue Responses" value={kpiOverdue} warning={kpiOverdue > 0} />
      </div>

      <StateView query={jobsQuery} empty="No PPRs match the current filters.">
        {() => (
          <div className="ppr-board">
            {visibleStatuses.map((status) => {
              const list = grouped.get(status) ?? [];
              return (
                <div key={status} className="ppr-col">
                  <div className="ppr-col-header">
                    <span className="ppr-col-title">{status}</span>
                    <span className="ppr-col-count">{list.length}</span>
                  </div>
                  {list.length === 0 && <div className="ppr-empty">No PPRs</div>}
                  {list.map((job) => (
                    <JobCard key={job.id} job={job} />
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </StateView>

      {modalOpen && <StartPprModal onClose={() => setModalOpen(false)} />}
    </>
  );
}

function Kpi({ label, value, warning }: { label: string; value: number; warning?: boolean }) {
  return (
    <Card>
      <div className="ppr-kpi-label">{label}</div>
      <div className={`ppr-kpi-value${warning ? ' ppr-kpi-value--warning' : ''}`}>{value}</div>
    </Card>
  );
}

function JobCard({ job }: { job: PprJob }) {
  const daysOld = daysSince(job.createdAt) ?? 0;
  const aging = (job.contactsOverdue ?? 0) > 0;
  const escalated = (job.contactsEscalated ?? 0) > 0;

  return (
    <Link
      to={`/ppr/${job.id}`}
      className={`ppr-card${escalated ? ' ppr-card-escalated' : aging ? ' ppr-card-aging' : ''}`}
    >
      <div className="ppr-card-top">
        <div className="ppr-card-job">
          {job.jobName}
          <small>#{job.jobNumber} · {fmtUsd(job.jobValue)}</small>
        </div>
      </div>
      <div className="ppr-card-meta">
        <Badge label={job.reviewType} tone={REVIEW_TYPE_TONE[job.reviewType]} />
        <Badge label={job.status} tone={STATUS_TONE[job.status]} />
        {escalated && (
          <Badge label={`⚠ ${job.contactsEscalated} escalated`} tone="danger" />
        )}
      </div>
      <div className="ppr-card-stats">
        <span>
          {job.contactsResponded ?? 0} of {job.contactsTotal ?? 9} responded
        </span>
        {/* Field-leader team feedback, called out separately from the responded
            count because it's a separate deliverable and the product owner wants it visible
            without opening each review. Only shown when outstanding — a marker on
            every finished job is noise. */}
        {!job.teamFeedbackComplete && (
          <span title="The field leader hasn't recorded feedback on their team yet">
            · team feedback outstanding
          </span>
        )}
        <span>{daysOld}d old · closed {fmtDate(job.closeDate)}</span>
      </div>
    </Link>
  );
}
